import * as React from 'react';
import type { StyleProp, ViewStyle } from 'react-native';
import { Platform, Pressable } from 'react-native';
import { useStyles } from 'react-native-unistyles';
import * as UToolTip from 'universal-tooltip';

import { Icon } from '../icon';
import { Info } from '../icons';

interface TooltipProps {
  text: string;
  children?: React.ReactNode;
  onPress?: () => void;
  openOnHover?: boolean;
  style?: StyleProp<ViewStyle>;
}

export const Tooltip = ({
  text,
  children,
  onPress,
  openOnHover = false,
}: TooltipProps) => {
  const { theme } = useStyles();
  const [open, setOpen] = React.useState(false);

  const trigger = children ?? (
    <Icon
      type="naked"
      icon={<Info />}
      fillColor={theme.colors.icon.color}
      width={15}
      height={15}
    />
  );

  return (
    <UToolTip.Root
      open={open}
      onDismiss={() => {
        setOpen(false);
      }}
    >
      <UToolTip.Trigger>
        <Pressable
          onPress={() => {
            setOpen(true);
          }}
          onHoverIn={
            openOnHover && Platform.OS === 'web'
              ? () => {
                  setOpen(true);
                }
              : undefined
          }
          onHoverOut={
            openOnHover && Platform.OS === 'web'
              ? () => {
                  setOpen(false);
                }
              : undefined
          }
        >
          {onPress ? (
            <Pressable
              onPress={onPress}
              style={({ pressed }) => ({ opacity: pressed ? 0.6 : 1 })}
            >
              {trigger}
            </Pressable>
          ) : (
            trigger
          )}
        </Pressable>
      </UToolTip.Trigger>

      {/* Portal so the tooltip escapes any overflow:hidden / scroll clipping
          from ancestor containers (z-index alone can't escape an overflow
          clip). No-op Fragment on native. */}
      <UToolTip.Portal>
        <UToolTip.Content
          // universal-tooltip always renders an extra (empty, transparent)
          // View absolutely positioned over the trigger on native, which
          // inherits this `style` regardless of open state - so anything
          // visible (border, etc.) here must stay web-only.
          style={
            Platform.OS === 'web'
              ? {
                  zIndex: 10000,
                  borderWidth: 1,
                  borderStyle: 'solid',
                  borderColor: theme.colors.dialog.borderColor,
                  borderRadius: 12,
                }
              : undefined
          }
          sideOffset={3}
          align="start"
          containerStyle={{
            paddingLeft: 16,
            paddingRight: 16,
            paddingTop: 16,
            paddingBottom: 16,
          }}
          onTap={() => {
            setOpen(false);
          }}
          onPointerDownOutside={() => {
            setOpen(false);
          }}
          dismissDuration={500}
          disableTapToDismiss
          side="top"
          presetAnimation="fadeIn"
          backgroundColor={theme.colors.card.default.backgroundColor}
          borderRadius={12}
          maxWidth={200}
        >
          <UToolTip.Text
            text={text}
            style={{ color: theme.colors.text.primary, fontSize: 12 }}
          />
        </UToolTip.Content>
      </UToolTip.Portal>
    </UToolTip.Root>
  );
};
